import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Page from "components/common_components/Page/Page";
import Header from "components/common_components/Header";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import Status from "components/common_components/Status";
import Money from "components/common_components/Money";
import ApplyPaymentDialog from "./ApplyPaymentDialog";
import { Grid, Paper, Typography, Divider, Link } from "@material-ui/core";
import { dateToString } from "utils/dateUtils";

const paymentStatusMap = {
    PENDING: { category: "warning", label: "Pending" },
    FAILED: { category: "error", label: "Failed" },
    SUCCEEDED: { category: "success", label: "Succeeded" },
    REFUNDED: { category: "info", label: "Refunded" },
};

const applicationStatusMap = {
    PENDING: { category: "warning", label: "Pending" },
    PARTIAL: { category: "info", label: "Partial" },
    COMPLETED: { category: "success", label: "Completed" },
};

function DetailField({ label, children }) {
    return (
        <div className="cp-payment-detail__field">
            <Typography variant="caption" color="textSecondary" className="cp-payment-detail__label">
                {label}
            </Typography>
            <Typography variant="body1" className="cp-payment-detail__value">
                {children || "—"}
            </Typography>
        </div>
    );
}

function ClientPortalPaymentDetail() {
    const history = useHistory();
    const location = useLocation();
    const payment = location.state?.cpPayment;
    const cpPaymentsFilters = location.state?.cpPaymentsFilters;
    const [applyDialogOpen, setApplyDialogOpen] = useState(false);

    function handleGoBack() {
        history.push("/", {
            cpPaymentsTab: true,
            cpPaymentsFilters,
        });
    }

    if (!payment) {
        return (
            <Page className="cp-payment-detail-page">
                <Header title="Payment Detail">
                    <Button outline small onClick={handleGoBack}>
                        <Icon name="arrow" size="md" color="primary" />
                        <span>Back</span>
                    </Button>
                </Header>
                <div className="layout">
                    <Typography variant="body1">Payment not found. Please go back and try again.</Typography>
                </div>
            </Page>
        );
    }

    const statusMapped = paymentStatusMap[payment.status];
    const appStatusMapped = applicationStatusMap[payment.applicationStatus];

    return (
        <Page className="cp-payment-detail-page">
            <Header title="Payment Detail">
                <Button primary small onClick={() => setApplyDialogOpen(true)}>
                    <span>Apply</span>
                </Button>
                <Button outline small onClick={handleGoBack}>
                    <Icon name="arrow" size="md" color="primary" />
                    <span>Back</span>
                </Button>
            </Header>
            <div className="layout">
                <Paper className="cp-payment-detail" elevation={1}>
                    <div className="cp-payment-detail__section">
                        <Typography variant="h6" gutterBottom>
                            General Information
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Payment ID">{payment.paymentId}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Internal ID">{payment.id}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Payment Status">
                                    {statusMapped ? (
                                        <Status
                                            id={(payment.status || "").toLowerCase()}
                                            category={statusMapped.category}
                                            label={statusMapped.label}
                                        />
                                    ) : (
                                        payment.status
                                    )}
                                </DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Application Status">
                                    {appStatusMapped ? (
                                        <Status
                                            id={(payment.applicationStatus || "").toLowerCase()}
                                            category={appStatusMapped.category}
                                            label={appStatusMapped.label}
                                        />
                                    ) : (
                                        payment.applicationStatus || "—"
                                    )}
                                </DetailField>
                            </Grid>
                        </Grid>
                    </div>

                    <div className="cp-payment-detail__section">
                        <Typography variant="h6" gutterBottom>
                            Client
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <DetailField label="Client Name">{payment.clientFullName}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <DetailField label="Client ID">{payment.clientId}</DetailField>
                            </Grid>
                        </Grid>
                    </div>

                    <div className="cp-payment-detail__section">
                        <Typography variant="h6" gutterBottom>
                            Payment Details
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Amount to Pay (USD)">
                                    <Money amount={payment.amountToPayUsd} />
                                </DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Amount Paid (USD)">
                                    <Money amount={payment.amountPaidUsd} />
                                </DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Amount Refunded (USD)">
                                    <Money amount={payment.amountRefundedUsd} />
                                </DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Currency">{payment.currency}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Provider">{payment.paymentsProvider}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DetailField label="Description">{payment.description}</DetailField>
                            </Grid>
                            {payment.receiptUrl && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <DetailField label="Receipt">
                                        <Link href={payment.receiptUrl} target="_blank" rel="noopener noreferrer">
                                            View Receipt
                                        </Link>
                                    </DetailField>
                                </Grid>
                            )}
                        </Grid>
                    </div>

                    <div className="cp-payment-detail__section">
                        <Typography variant="h6" gutterBottom>
                            Dates
                        </Typography>
                        <Divider style={{ marginBottom: 16 }} />
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <DetailField label="Payment Created">{dateToString(payment.paymentCreationDatetime)}</DetailField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <DetailField label="Status Confirmed">{dateToString(payment.statusConfirmationDatetime)}</DetailField>
                            </Grid>
                        </Grid>
                    </div>

                    {payment.status === "FAILED" && payment.failureMessage && (
                        <div className="cp-payment-detail__section">
                            <Typography variant="h6" gutterBottom>
                                Failure Information
                            </Typography>
                            <Divider style={{ marginBottom: 16 }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <DetailField label="Failure Message">
                                        <Typography variant="body2" color="error">
                                            {payment.failureMessage}
                                        </Typography>
                                    </DetailField>
                                </Grid>
                            </Grid>
                        </div>
                    )}
                </Paper>
            </div>
            <ApplyPaymentDialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} payment={payment} />
        </Page>
    );
}

export default ClientPortalPaymentDetail;
